import React, { useState } from "react";
import { CaretUp, CaretDown, PencilSimple, Trash, Eye, FilmSlate, Image as ImageIcon, TextAa, CheckCircle, XCircle, ArrowsClockwise, Signpost, LinkBreak, Warning, Bell } from "@phosphor-icons/react";
import { deleteShot, moveShot, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const GEN_STATUS_STYLES = {
  queued: { label: "QUEUED", cls: "text-slate-500 bg-slate-800/50 border-slate-700", Icon: null },
  generating: { label: "GENERATING…", cls: "text-blue-400 bg-blue-400/10 border-blue-400/20", Icon: ArrowsClockwise },
  passed: { label: "PASSED", cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20", Icon: CheckCircle },
  failed_red: { label: "FAILED (RED)", cls: "text-rose-500 bg-rose-500/10 border-rose-500/20", Icon: XCircle },
  detour_bin: { label: "DETOUR BIN", cls: "text-fuchsia-400 bg-fuchsia-400/10 border-fuchsia-400/20", Icon: Signpost },
  blocked_on_dependency: { label: "BLOCKED (DEPENDENCY)", cls: "text-amber-400 bg-amber-400/10 border-amber-400/20", Icon: LinkBreak },
  pending_approval: { label: "PENDING APPROVAL", cls: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20", Icon: Bell },
  needs_ref_priority: { label: "NEEDS REF PRIORITY", cls: "text-orange-400 bg-orange-400/10 border-orange-400/20", Icon: Warning },
};

export default function ShotRow({ shot, isLocked, onEdit, onDeleted, onMoved, selectable, selected, onToggleSelect, onOpenAttempt }) {
  const [busy, setBusy] = useState(false);

  const handleMove = async (direction) => {
    setBusy(true);
    try {
      const res = await moveShot(shot.id, direction);
      if (res.moved) onMoved();
    } catch (e) {
      toast.error("Failed to reorder shot");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteShot(shot.id);
      toast.success(`Shot S${String(shot.scene_number).padStart(2, "0")}_SH${String(shot.shot_number).padStart(2, "0")} deleted`);
      onDeleted(shot.id);
    } catch (e) {
      toast.error("Failed to delete shot");
    } finally {
      setBusy(false);
    }
  };

  const shotCode = `S${String(shot.scene_number).padStart(2, "0")}_SH${String(shot.shot_number).padStart(2, "0")}`;
  const genStatus = GEN_STATUS_STYLES[shot.gen_status] || GEN_STATUS_STYLES.queued;
  const GenIcon = genStatus.Icon;

  return (
    <div
      data-testid={`shot-row-${shot.id}`}
      className="grid grid-cols-12 gap-4 items-start border-b border-slate-800 py-4 px-2 hover:bg-slate-800/30 transition-colors border-l-2 border-l-transparent hover:border-l-blue-500"
    >
      <div className="col-span-1 flex items-start gap-2">
        {selectable && (
          <input
            data-testid={`shot-select-checkbox-${shot.id}`}
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(shot.id)}
            className="mt-0.5 accent-blue-600"
          />
        )}
        <span className="font-mono text-xs font-bold text-slate-300">{shotCode}</span>
      </div>
      <div className="col-span-2">
        <button
          data-testid={`shot-thumbnail-${shot.id}`}
          onClick={() => shot.latest_image_path && onOpenAttempt(shot)}
          className="w-full h-16 bg-slate-800/50 border border-slate-800 rounded-sm flex items-center justify-center text-slate-600 overflow-hidden relative"
        >
          {shot.latest_video_path ? (
            <>
              <video src={fileUrl(shot.latest_video_path)} className="w-full h-full object-cover" muted />
              <FilmSlate size={14} className="absolute bottom-1 right-1 text-white drop-shadow" />
            </>
          ) : shot.latest_image_path ? (
            <img src={fileUrl(shot.latest_image_path)} alt={shotCode} className="w-full h-full object-cover" />
          ) : shot.output_type === "clip" ? (
            <FilmSlate size={20} />
          ) : (
            <ImageIcon size={20} />
          )}
        </button>
      </div>
      <div className="col-span-4 space-y-1">
        <p className="text-sm text-slate-200">{shot.pose_action}</p>
        <p className="text-xs text-slate-500">
          <span className="text-slate-400">{shot.characters.join(", ")}</span> · {shot.setting}
        </p>
        {shot.mood_sfx && <p className="text-xs text-slate-600 italic">{shot.mood_sfx}</p>}
      </div>
      <div className="col-span-3 flex flex-wrap gap-1 content-start">
        {shot.is_pov && (
          <span data-testid={`shot-pov-badge-${shot.id}`} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 flex items-center gap-1">
            <Eye size={10} /> POV
          </span>
        )}
        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm text-slate-400 bg-slate-800/50 border border-slate-700">
          {shot.camera_angle}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm text-blue-400 bg-blue-400/10 border border-blue-400/20">
          {shot.output_type}
        </span>
        {shot.text_in_post && (
          <span data-testid={`shot-text-in-post-badge-${shot.id}`} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm text-amber-400 bg-amber-400/10 border border-amber-400/20 flex items-center gap-1">
            <TextAa size={10} /> Text in Post
          </span>
        )}
        {shot.gen_status && shot.gen_status !== "queued" && (
          <button
            data-testid={`shot-gen-status-badge-${shot.id}`}
            onClick={() => shot.latest_attempt_id && onOpenAttempt(shot)}
            className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border flex items-center gap-1 ${genStatus.cls}`}
          >
            {GenIcon && <GenIcon size={10} />} {genStatus.label}
            {shot.retry_count > 0 && ` · ${shot.retry_count}x`}
            {shot.total_cost > 0 && ` · $${shot.total_cost.toFixed(3)}`}
          </button>
        )}
      </div>
      <div className="col-span-1 flex items-center gap-1 justify-end">
        {!isLocked && (
          <>
            <button data-testid={`shot-move-up-btn-${shot.id}`} disabled={busy} onClick={() => handleMove("up")} className="text-slate-600 hover:text-slate-200 transition-colors">
              <CaretUp size={14} />
            </button>
            <button data-testid={`shot-move-down-btn-${shot.id}`} disabled={busy} onClick={() => handleMove("down")} className="text-slate-600 hover:text-slate-200 transition-colors">
              <CaretDown size={14} />
            </button>
            <button data-testid={`shot-edit-btn-${shot.id}`} disabled={busy} onClick={() => onEdit(shot)} className="text-slate-600 hover:text-blue-400 transition-colors">
              <PencilSimple size={14} />
            </button>
            <button data-testid={`shot-delete-btn-${shot.id}`} disabled={busy} onClick={handleDelete} className="text-slate-600 hover:text-rose-500 transition-colors">
              <Trash size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
